import { Component, ElementRef, ViewChild, inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { IHeaderAngularComp } from "ag-grid-angular";
import { IHeaderParams } from "ag-grid-community";
import { CheckboxChangeEvent } from "@openng/optimus-ui/checkbox";
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Utils } from "src/modules/utils/shared/utils";
import { TableColumn } from "../../netgen/TableColumn";
import { SmTableComponent } from "../sm-table/sm-table.component";
import { Strings } from "src/modules/utils/shared/strings";
import { TableColumnActionButton } from "../../models/table-column-action-button.model";

@Component({
    selector: "sm-table-header",
    templateUrl: "./sm-table-header.component.html",
    styleUrl: "./sm-table-header.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SmTableHeaderComponent implements IHeaderAngularComp {
    _Utils = Utils;

    app = inject(MainAppService);

    params: IHeaderParams;
    table: SmTableComponent;
    tableColumn: TableColumn;
    headerCheckboxState = {
        value: false,
        indeterminate: false,
        disabled: true,
    };

    @ViewChild("menuButton", { read: ElementRef })
    public menuButton!: ElementRef;

    agInit(params: IHeaderParams): void {
        this.params = params;
        this.table = params.context.table;
        this.tableColumn = params.column?.getColDef()?.context?.tableColumn;
        this.updateHeaderCheckboxState();
    }

    isFirstColumn(): boolean {
        return Arrays.getSafe(this.params.api.getAllDisplayedColumns() as [], 0) ==
            this.params.column;
    }

    getStyleClass(): string {
        let a = this.tableColumn?.headerAlign ?? this.table?.headerAlign;
        return Strings.combine(
                this.tableColumn?.headerStyleClass,
                a == "center"
                    ? "ag-center-aligned-header "
                    : a == "right"
                      ? "ag-right-aligned-header "
                      : "",
                " ",
            ) ?? "";
    }

    menuClicked(event: any): void {
        this.table.openSettings(event);
        event.stopPropagation();
        event.preventDefault();
    }

    async actionButtonClicked(button: TableColumnActionButton, event: MouseEvent): Promise<void> {
        event.stopPropagation();
        event.preventDefault();
        await button.action(this.tableColumn, this.table);
        this.params.api.refreshHeader();
    }

    headerContextMenu(event: MouseEvent): void {
        if (!this.table.useColumnMenu) {
            return;
        }

        this.openColumnSettings(event);
        event.preventDefault();
    }

    sortRequested(event: PointerEvent): void {
        if (this.params.column.getColDef().sortable) {
            this.params.setSort(this.params.column.getSort() == "asc" ? "desc" : "asc", event.shiftKey);
        }
    }

    refresh(params: IHeaderParams): boolean {
        this.agInit(params);
        return true;
    }

    updateHeaderCheckboxState(): void {
        if (this.tableColumn?.headerCheckbox) {
            this.headerCheckboxState = this.table.getBooleanHeaderCheckboxState(this.tableColumn, this.params.api);
        }
    }

    headerCheckboxClicked(event: MouseEvent): void {
        event.stopPropagation();
    }

    headerCheckboxChanged(event: CheckboxChangeEvent): void {
        event.originalEvent?.stopPropagation();
        this.table.setBooleanHeaderCheckboxValue(this.tableColumn, event.checked === true, this.params.api);
        this.updateHeaderCheckboxState();
    }

    openColumnSettings(event: any): void {
        this.app.openContextMenu([
            this.params.column.getPinned() != "left"
                ? {
                      label: this.app.t("table.pinLeft"),
                      command: () => this.pinLeft(event),
                  }
                : null,
            this.params.column.getPinned() == "left"
                ? {
                      label: this.app.t("table.unpin"),
                      command: () => this.unpin(event),
                  }
                : null,
            this.table.isEnterprise &&
            !this.table.getGroupColumnIds().includes(this.params.column.getColId())
                ? {
                      label: "Nach dieser Spalte gruppieren",
                      command: () =>
                          this.table.addGroupColumn(this.params.column.getColId()),
                  }
                : null,
            this.table.getGroupColumnIds().includes(this.params.column.getColId())
                ? {
                      label: "Gruppierung entfernen",
                      command: () =>
                          this.table.removeGroupColumn(this.params.column.getColId()),
                  }
                : null,
        ]);
        event.stopPropagation();
    }

    pinLeft(_: any): void {
        this.params.api.applyColumnState({
            state: [{ colId: this.params.column.getColId(), pinned: "left" }],
        });
    }

    unpin(_: any): void {
        this.params.api.applyColumnState({
            state: [{ colId: this.params.column.getColId(), pinned: null }],
        });
    }

    click(event: any): void {
        let tc = this.params.column.getColDef().context
            .tableColumn as TableColumn;
        if (!tc.headerClicked?.(tc.id)) {
            event.stopPropagation();
            event.preventDefault();
        }
    }
}
